import type { TiebreakerRuleSetSnapshot } from '../../shared/league-structure/types';

type JsonRecord = Record<string, unknown>;

export function extractNflTiebreakerRuleSet(value: unknown): TiebreakerRuleSetSnapshot | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const directRuleSet = extractFromStructure(readProperty(value, 'structure', 'Structure'));
  if (directRuleSet) {
    return directRuleSet;
  }

  const modelRuleSet = extractNflTiebreakerRuleSet(readProperty(value, 'model', 'Model'));
  if (modelRuleSet) {
    return modelRuleSet;
  }

  const leagueRuleSet = extractNflTiebreakerRuleSet(readProperty(value, 'league', 'League'));
  if (leagueRuleSet) {
    return leagueRuleSet;
  }

  return undefined;
}

function extractFromStructure(structure: unknown): TiebreakerRuleSetSnapshot | undefined {
  if (!isRecord(structure)) {
    return undefined;
  }

  const ruleSet = readProperty(structure, 'tiebreakerRuleSet', 'TiebreakerRuleSet');
  return normalizeRuleSet(ruleSet);
}

function normalizeRuleSet(ruleSet: unknown): TiebreakerRuleSetSnapshot | undefined {
  if (!isRecord(ruleSet)) {
    return undefined;
  }

  const rawPaths = readProperty(ruleSet, 'paths', 'Paths');
  if (!isRecord(rawPaths)) {
    return undefined;
  }

  return {
    name: readString(ruleSet, 'name', 'Name'),
    restartOnPartialBreak: readBoolean(ruleSet, 'restartOnPartialBreak', 'RestartOnPartialBreak'),
    paths: {
      divisional: readStringArray(rawPaths, 'divisional', 'Divisional'),
      wildcard: readStringArray(rawPaths, 'wildcard', 'Wildcard'),
      conference: readStringArray(rawPaths, 'conference', 'Conference'),
    },
  };
}

function readProperty(record: JsonRecord, camelCase: string, pascalCase: string): unknown {
  return record[camelCase] ?? record[pascalCase];
}

function readString(record: JsonRecord, camelCase: string, pascalCase: string): string {
  const value = readProperty(record, camelCase, pascalCase);
  return typeof value === 'string' ? value : '';
}

function readBoolean(record: JsonRecord, camelCase: string, pascalCase: string): boolean {
  return readProperty(record, camelCase, pascalCase) === true;
}

function readStringArray(record: JsonRecord, camelCase: string, pascalCase: string): string[] {
  const value = readProperty(record, camelCase, pascalCase);
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function isRecord(value: unknown): value is JsonRecord {
  return value !== null && typeof value === 'object';
}
